import React from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { makeStyles } from '@material-ui/core/styles'
import ButtonBase from '@material-ui/core/ButtonBase'
import Typography from '@material-ui/core/Typography'
import ChevronRightIcon from 'components/material-symbol/icons/ChevronRight'

const useStyles = makeStyles(theme => ({
  root: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    gap: theme.spacing(0.75),
    padding: theme.spacing(2),
    minHeight: 72,
    minWidth: 0,
    boxSizing: 'border-box',
    textAlign: 'left',
    border: '1px solid transparent',
    borderRadius: 12,
    backgroundColor: '#F4F7F9',
    '&:hover': {
      backgroundColor: '#E9EEF2',
    },
    '&.Mui-focusVisible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.5),
    },
  },
  cardSelected: {
    borderColor: theme.palette.primary.main,
    backgroundColor: '#E0EAFF',
    '&:hover': {
      backgroundColor: '#D4E2FF',
    },
  },
  label: {
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.06em',
    lineHeight: 1.3,
    textTransform: 'uppercase',
    color: '#6B7C87',
    [theme.breakpoints.down('xs')]: {
      letterSpacing: '0.04em',
    },
  },
  count: {
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.3,
    color: '#00205B',
  },
  hint: {
    fontSize: '0.8125rem',
    fontWeight: 400,
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  },
  followUps: {
    position: 'relative',
    paddingRight: theme.spacing(5),
    textDecoration: 'none',
    backgroundColor: '#001C57',
    '&:hover': {
      backgroundColor: '#0A2E78',
    },
    [theme.breakpoints.down('xs')]: {
      paddingRight: theme.spacing(4),
    },
  },
  followUpsLabel: {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  followUpsTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    lineHeight: 1.3,
    color: theme.palette.common.white,
  },
  followUpsHint: {
    color: 'rgba(255, 255, 255, 0.75)',
  },
  followUpsChevron: {
    position: 'absolute',
    top: '50%',
    right: theme.spacing(1.5),
    transform: 'translateY(-50%)',
    color: theme.palette.common.white,
  },
}))

const plural = (count, singular, pluralForm) =>
  count === 1 ? singular : pluralForm

const cards = ({ attention, due, fresh }) => [
  {
    value: 'attention',
    label: 'Needs attention',
    count: attention,
    hint: plural(attention, 'student to review', 'students to review'),
  },
  {
    value: 'DUE',
    label: 'Due to rebook',
    count: due,
    hint: 'past booking pace',
  },
  {
    value: 'NEW',
    label: 'New students',
    count: fresh,
    hint: plural(fresh, 'needs a welcome', 'need a welcome'),
  },
]

export const countStudentInsights = students =>
  students.reduce(
    (counts, { insights }) => ({
      attention: counts.attention + (insights?.needsAttention ? 1 : 0),
      due: counts.due + (insights?.status === 'DUE' ? 1 : 0),
      fresh: counts.fresh + (insights?.status === 'NEW' ? 1 : 0),
    }),
    { attention: 0, due: 0, fresh: 0 },
  )

// Selecting the active card again clears the engagement status filter.
export default function StudentInsightsSummary({
  students,
  statusFilter,
  onSelect,
}) {
  const classes = useStyles()

  return (
    <div className={classes.root} data-testid="student-insights-summary">
      {cards(countStudentInsights(students)).map(card => {
        const selected = statusFilter === card.value

        return (
          <ButtonBase
            key={card.value}
            className={`${classes.card} ${
              selected ? classes.cardSelected : ''
            }`}
            aria-pressed={selected}
            onClick={() => onSelect(selected ? 'all' : card.value)}
            data-testid={`student-insights-summary-${card.value}`}
          >
            <Typography component="span" className={classes.label}>
              {card.label}
            </Typography>
            <Typography component="span" className={classes.count}>
              {card.count}
            </Typography>
            <Typography component="span" className={classes.hint}>
              {card.hint}
            </Typography>
          </ButtonBase>
        )
      })}
      <ButtonBase
        component={Link}
        role={undefined}
        to="/pga-coach/students/follow-ups"
        className={`${classes.card} ${classes.followUps}`}
        data-testid="student-insights-summary-follow-ups"
      >
        <Typography
          component="span"
          className={`${classes.label} ${classes.followUpsLabel}`}
        >
          Follow-ups
        </Typography>
        <Typography component="span" className={classes.followUpsTitle}>
          Review activity
        </Typography>
        <Typography
          component="span"
          className={`${classes.hint} ${classes.followUpsHint}`}
        >
          Automations and results
        </Typography>
        <ChevronRightIcon className={classes.followUpsChevron} />
      </ButtonBase>
    </div>
  )
}

StudentInsightsSummary.propTypes = {
  students: PropTypes.arrayOf(
    PropTypes.shape({
      insights: PropTypes.shape({
        status: PropTypes.string,
        needsAttention: PropTypes.bool,
      }),
    }),
  ).isRequired,
  statusFilter: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
}
